import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar, Box, Chip, Collapse, Drawer, IconButton, Stack, Typography } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'

import LoadingSwitch from '@/components/ui/switchs/LoadingSwitch'
import type { Policy } from '@/core/api/policy'
import { formatDroits } from '@/utils/authz/labels'

type DrawerLicense = {
  id: string
  isAssigned?: boolean
  isMissingSeat?: boolean
  isAssigning?: boolean
  name: string
  description?: string
  availableQuantity?: number
  policies?: Policy[]
}

type AssignLicenseDrawerProps = {
  licenses: DrawerLicense[]
  open: boolean
  onAssignLicense?: (licenseId: string, isAssigned: boolean) => void
  onClose?: () => void
  title: string
  identifier?: string
}

function getInitials(name: string): string {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
  return initials || '?'
}

function PolicyDroits({ policy }: { policy: Policy }) {
  const { t } = useTranslation()
  const droits = formatDroits(policy.permissions, t)

  return (
    <Box sx={{ borderLeft: '2px solid', borderColor: 'primary.light', pl: 2, py: 0.5 }} data-testid={`policy-droits-${policy.name}`}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, wordBreak: 'break-word', mb: 0.5 }}>
        {policy.description || policy.name}
      </Typography>
      <Stack spacing={0.5}>
        {droits.map((droit) => (
          <Stack key={droit.resource} direction="row" alignItems="center" flexWrap="wrap" gap={0.5}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mr: 0.5 }}>
              {droit.resource} :
            </Typography>
            {droit.actions.map((action) => (
              <Chip
                key={action.key}
                label={action.label}
                size="small"
                color={action.key === 'manage' ? 'primary' : 'default'}
                variant={action.key === 'manage' ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}

function LicenseRow({
  license,
  expanded,
  onToggle,
  onAssignLicense,
}: {
  license: DrawerLicense
  expanded: boolean
  onToggle: () => void
  onAssignLicense?: AssignLicenseDrawerProps['onAssignLicense']
}) {
  const { t } = useTranslation()
  const policies = license.policies ?? []

  return (
    <Box
      data-testid={`license-row-${license.name}`}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'border-color 0.2s',
        '&:hover': { borderColor: 'primary.light' },
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ p: 1.5 }}>
        <IconButton
          size="small"
          onClick={onToggle}
          aria-label={t('drawer.assignLicenses.toggleDetails')}
          data-testid={`license-expand-${license.name}`}
          sx={{ mr: 1, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
        >
          <ExpandMoreRoundedIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {license.name}
          </Typography>
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.75}>
            {license.availableQuantity !== undefined ? (
              <Typography variant="caption" color="text.secondary">
                {t('common:available', { count: license.availableQuantity, context: `${license.availableQuantity}` })}
              </Typography>
            ) : null}
            {license.isMissingSeat ? (
              <Chip
                label={t('drawer.assignLicenses.missingSeat')}
                size="small"
                color="warning"
                variant="outlined"
                data-testid={`license-missing-seat-${license.name}`}
              />
            ) : null}
          </Stack>
        </Box>
        <LoadingSwitch
          disabled={license.availableQuantity === 0 && !license.isAssigned}
          edge="end"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onAssignLicense?.(license.id, event.target.checked)
          }}
          checked={license.isAssigned}
          loading={license.isAssigning}
          data-testid={`license-toggle-${license.name}`}
        />
      </Stack>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ bgcolor: 'action.hover', borderTop: '1px solid', borderColor: 'divider', px: { xs: 2, sm: 3 }, py: 2 }}>
          {license.description ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {license.description}
            </Typography>
          ) : null}
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.1em' }}>
            {t('drawer.assignLicenses.habilitations')}
          </Typography>
          {policies.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('drawer.assignLicenses.noHabilitation')}
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {policies.map((policy) => (
                <PolicyDroits key={policy._id} policy={policy} />
              ))}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

export default function AssignLicenseDrawer(props: AssignLicenseDrawerProps) {
  const { licenses, open, onAssignLicense, onClose, title, identifier } = props
  const { t } = useTranslation()

  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) setExpandedId(null)
  }, [open])

  return (
    <Drawer anchor="right" open={open} onClose={onClose} sx={{ zIndex: (theme) => theme.zIndex.drawer + 10 }}>
      <Stack direction="column" sx={{ height: '100%', width: { xs: '100vw', sm: '500px', md: '600px' }, maxWidth: '100vw', boxSizing: 'border-box' }}>
        {/* Header */}
        <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 3 } }}>
          <Stack direction="row" justifyContent="flex-end">
            <IconButton onClick={onClose} sx={{ color: 'inherit' }} aria-label={t('common:button.close.label', { defaultValue: 'Fermer' })}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit', width: 56, height: 56, fontWeight: 700 }}>{getInitials(title)}</Avatar>
            <Box>
              <Typography component="h2" variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {title}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                {identifier ? `${t('drawer.assignLicenses.identifier')} : ${identifier}` : t('drawer.assignLicenses.subtitle')}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Body */}
        <Box flex={1} sx={{ overflowY: 'auto', px: { xs: 2, sm: 4 }, py: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <Box sx={{ width: 6, height: 24, bgcolor: 'primary.main', borderRadius: 1 }} />
            <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: '0.12em' }}>
              {t('drawer.assignLicenses.subtitle')}
            </Typography>
          </Stack>
          <Stack spacing={1.5}>
            {licenses.map((license) => (
              <LicenseRow
                key={license.id}
                license={license}
                expanded={expandedId === license.id}
                onToggle={() => setExpandedId((prev) => (prev === license.id ? null : license.id))}
                onAssignLicense={onAssignLicense}
              />
            ))}
          </Stack>
        </Box>
      </Stack>
    </Drawer>
  )
}
